import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Card, Row, Col, Badge, ProgressBar, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import {
  X,
  CheckCircle,
  AlertCircle,
  FileText,
  Receipt,
  TrendingUp,
  TrendingDown,
  Calendar,
  Hash,
  DollarSign,
  Building,
  User,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * Transaction Details Modal
 * Shows transaction details and matching suggestions for bank statements
 */
const TransactionDetailsModal = ({ show, onHide, transaction, onMatchSuccess, onCreateVerification }) => {
  const { companyId } = useParams();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);

  useEffect(() => {
    if (show && transaction) {
      fetchSuggestions();
    }
  }, [show, transaction]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/banking/statements/${transaction.id}/unified-suggestions`
      );
      const response = await axios.get(apiUrl, {
        withCredentials: true,
        params: { limit: 5 }
      });

      if (response.data.success) {
        setSuggestions(response.data.suggestions);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Kunde inte hämta matchningsförslag');
    } finally {
      setLoading(false);
    }
  };

  const handleMatch = async (suggestion) => {
    setMatching(true);
    setError(null);

    try {
      let apiUrl;
      let requestBody;

      // Different API endpoints based on match type
      if (suggestion.matchStrategy === 'customer_invoice') {
        apiUrl = getApiUrl(
          `${companyId}/accounting/reconciliation/banking/statements/${transaction.id}/match-customer-invoice`
        );
        requestBody = {
          invoiceId: suggestion.candidateId,
          createPaymentRecord: true
        };
      } else if (suggestion.matchStrategy === 'supplier_invoice') {
        apiUrl = getApiUrl(
          `${companyId}/accounting/reconciliation/banking/statements/${transaction.id}/match`
        );
        requestBody = {
          invoiceId: suggestion.candidateId,
          matchType: 'supplier_invoice'
        };
      } else if (suggestion.matchStrategy === 'verification') {
        apiUrl = getApiUrl(
          `${companyId}/accounting/reconciliation/banking/statements/${transaction.id}/match`
        );
        requestBody = {
          verificationId: suggestion.candidateId,
          matchType: 'verification'
        };
      }

      const response = await axios.post(apiUrl, requestBody, {
        withCredentials: true
      });

      if (response.data.success) {
        if (onMatchSuccess) {
          onMatchSuccess();
        }
        onHide();
      }
    } catch (err) {
      console.error('Error creating match:', err);
      setError(err.response?.data?.message || 'Kunde inte skapa matchning');
    } finally {
      setMatching(false);
    }
  };

  const getMatchTypeBadge = (strategy) => {
    const badges = {
      customer_invoice: { text: 'Kundfaktura', variant: 'success' },
      supplier_invoice: { text: 'Leverantörsfaktura', variant: 'primary' },
      verification: { text: 'Verifikat', variant: 'info' },
      invoice_verification_pair: { text: 'Faktura + Verifikat', variant: 'warning' }
    };

    const badge = badges[strategy] || { text: strategy, variant: 'secondary' };

    return (
      <Badge bg={badge.variant} style={{ fontSize: '0.75rem' }}>
        {badge.text}
      </Badge>
    );
  };

  const getConfidenceBadgeColor = (confidence) => {
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.75) return 'info';
    if (confidence >= 0.5) return 'warning';
    return 'secondary';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('sv-SE');
  };

  if (!transaction) return null;

  const isIncoming = parseFloat(transaction.amount) > 0;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: isIncoming ? '#f0fdf4' : '#fef2f2',
              color: isIncoming ? '#16a34a' : '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isIncoming ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
          </div>
          <div>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              Transaktionsdetaljer
            </span>
            <div style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>
              {isIncoming ? 'Inbetalning' : 'Utbetalning'}
            </div>
          </div>
          <div className="ms-auto">
            <Badge bg={transaction.reconciliation_status === 'unmatched' ? 'warning' : 'success'}>
              {transaction.reconciliation_status === 'unmatched' ? 'Omatchad' : 'Matchad'}
            </Badge>
          </div>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Close"
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {/* Transaction Info Card */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <Calendar size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Datum</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {formatDate(transaction.transaction_date)}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <DollarSign size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Belopp</strong>
                  </div>
                  <div
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: isIncoming ? '#16a34a' : '#dc2626',
                      paddingLeft: '24px'
                    }}
                  >
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <Hash size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Referens</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {transaction.reference || '-'}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <FileText size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Beskrivning</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {transaction.description || '-'}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <Building size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Konto</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {transaction.account_number}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <DollarSign size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Saldo</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {transaction.balance ? formatCurrency(transaction.balance) : '-'}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {/* Loading Suggestions */}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Laddar matchningsförslag...</span>
            </Spinner>
            <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Söker efter matchningar...
            </div>
          </div>
        )}

        {/* Suggestions */}
        {!loading && suggestions.length > 0 && (
          <Card>
            <Card.Header style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <strong style={{ fontSize: '1rem', color: '#1f2937' }}>Matchningsförslag</strong>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {suggestions.length} förslag hittades
              </div>
            </Card.Header>
            <Card.Body>
              {suggestions.map((suggestion, index) => (
                <SuggestionCard
                  key={index}
                  suggestion={suggestion}
                  onMatch={handleMatch}
                  matching={matching}
                  expanded={expandedSuggestion === index}
                  onToggleExpand={() => setExpandedSuggestion(expandedSuggestion === index ? null : index)}
                  getMatchTypeBadge={getMatchTypeBadge}
                  getConfidenceBadgeColor={getConfidenceBadgeColor}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                />
              ))}
            </Card.Body>
          </Card>
        )}

        {/* No Suggestions */}
        {!loading && suggestions.length === 0 && (
          <Alert variant="info">
            <AlertCircle size={16} className="me-2" />
            Inga matchningsförslag hittades. Du kan skapa ett verifikat manuellt.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={onHide} disabled={matching}>
          Stäng
        </Button>
        <Button
          variant="primary"
          onClick={() => onCreateVerification && onCreateVerification(transaction)}
          disabled={matching}
        >
          <FileText size={16} className="me-2" />
          Skapa Verifikat
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Suggestion Card Component
 * Displays individual match suggestion with confidence and actions
 */
const SuggestionCard = ({
  suggestion,
  onMatch,
  matching,
  expanded,
  onToggleExpand,
  getMatchTypeBadge,
  getConfidenceBadgeColor,
  formatCurrency,
  formatDate
}) => {
  return (
    <div
      className="mb-2"
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1rem',
        backgroundColor: expanded ? '#f9fafb' : 'white',
        transition: 'all 0.2s'
      }}
    >
      <Row className="align-items-center">
        <Col md={2}>
          <div className="text-center">
            <Badge bg={getConfidenceBadgeColor(suggestion.confidence)} style={{ fontSize: '0.875rem', padding: '0.5rem' }}>
              {(suggestion.confidence * 100).toFixed(0)}%
            </Badge>
            <div style={{ fontSize: '0.625rem', color: '#9ca3af', marginTop: '4px' }}>
              Säkerhet
            </div>
            <ProgressBar
              now={suggestion.confidence * 100}
              variant={getConfidenceBadgeColor(suggestion.confidence)}
              style={{ height: '4px', marginTop: '4px' }}
            />
          </div>
        </Col>
        <Col md={2}>
          {getMatchTypeBadge(suggestion.matchStrategy)}
        </Col>
        <Col md={4}>
          <div>
            <strong style={{ fontSize: '0.875rem', color: '#1f2937' }}>
              {suggestion.invoiceNumber || suggestion.verificationNumber}
            </strong>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {suggestion.matchReason}
            </div>
          </div>
        </Col>
        <Col md={2}>
          <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
            {formatCurrency(suggestion.amount)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {formatDate(suggestion.invoiceDate || suggestion.verificationDate)}
          </div>
        </Col>
        <Col md={2} className="text-end">
          <Button
            size="sm"
            variant="success"
            onClick={() => onMatch(suggestion)}
            disabled={matching}
            className="me-2"
          >
            <CheckCircle size={14} className="me-1" />
            Matcha
          </Button>
          <Button
            size="sm"
            variant="link"
            onClick={onToggleExpand}
            style={{ padding: '0.25rem 0.5rem' }}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </Col>
      </Row>

      {/* Expanded Details */}
      {expanded && (
        <Row className="mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
          <Col>
            <div style={{ fontSize: '0.875rem' }}>
              <strong>Detaljer:</strong>
              <div className="mt-2">
                {suggestion.customerName && (
                  <div>
                    <User size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                    Kund: {suggestion.customerName}
                  </div>
                )}
                {suggestion.supplierName && (
                  <div>
                    <Building size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                    Leverantör: {suggestion.supplierName}
                  </div>
                )}
                {suggestion.verificationNumber && (
                  <div>
                    <Receipt size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                    Verifikat: {suggestion.verificationNumber}
                  </div>
                )}
                {suggestion.ocrReference && (
                  <div>
                    <Hash size={14} className="me-2" style={{ verticalAlign: 'middle' }} />
                    OCR: {suggestion.ocrReference}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TransactionDetailsModal;
